import { cn } from '@commenda/utils';
import React from 'react';
import { Badge, BadgeProps } from '../Badge';
import { Icons } from '../Icons';
import {
  SettingLeadingTile,
  type SettingLeadingTileVariant,
} from './SettingLeadingTile';

export interface SettingRowProps {
  title: string;
  description?: string;
  descriptionVariant?: 'default' | 'error';
  badge?: string;
  badgeVariant?: BadgeProps['variant'];
  leadingIcon?: React.ReactNode;
  leadingTileVariant?: SettingLeadingTileVariant;
  trailing?: React.ReactNode;
  onNavigate?: () => void;
  disabled?: boolean;
  className?: string;
}

const ROW_BASE = 'flex min-h-16 w-full items-center gap-lg p-lg';

export const SettingRow = ({
  title,
  description,
  descriptionVariant = 'default',
  badge,
  badgeVariant = 'blue',
  leadingIcon,
  leadingTileVariant = 'default',
  trailing,
  onNavigate,
  disabled = false,
  className,
}: SettingRowProps) => {
  const body = (
    <>
      {leadingIcon ? (
        <SettingLeadingTile variant={leadingTileVariant}>
          {leadingIcon}
        </SettingLeadingTile>
      ) : null}

      <span className="flex min-w-0 flex-1 flex-col gap-xxs">
        <span className="flex items-center gap-sm">
          <span
            className={cn(
              'txt-small-medium truncate',
              disabled ? 'text-text-disabled' : 'text-text-primary'
            )}
          >
            {title}
          </span>
          {badge ? (
            <Badge variant={badgeVariant} size="sm">
              {badge}
            </Badge>
          ) : null}
        </span>
        {description ? (
          <span
            className={cn(
              'txt-extra-small-regular w-full',
              disabled
                ? 'text-text-disabled'
                : descriptionVariant === 'error'
                  ? 'text-text-error'
                  : 'text-text-tertiary'
            )}
          >
            {description}
          </span>
        ) : null}
      </span>

      {trailing || onNavigate ? (
        <span className="flex shrink-0 items-center gap-md pl-md">
          {trailing}
          {onNavigate ? (
            <Icons.ChevronRight
              className="size-4 shrink-0 text-fg-quaternary"
              aria-hidden="true"
            />
          ) : null}
        </span>
      ) : null}
    </>
  );

  if (onNavigate) {
    return (
      <button
        type="button"
        onClick={onNavigate}
        disabled={disabled}
        className={cn(
          ROW_BASE,
          'cursor-pointer bg-transparent text-left hover:bg-bg-secondary active:bg-bg-tertiary',
          'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-fg-brand',
          'disabled:cursor-default disabled:hover:bg-transparent',
          className
        )}
      >
        {body}
      </button>
    );
  }

  return <div className={cn(ROW_BASE, className)}>{body}</div>;
};
